import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box, Typography, Button, Chip, Stack, CircularProgress,
  Paper, Alert, Tooltip, Collapse,
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { getRunbooks, getRunbookHealth, triggerRunbook } from '../../api/client'
import type { RunbookHealthSummary, RunbookJobEntry, RunbookInfo } from '../../api/types'

// ── Helpers ───────────────────────────────────────────────────────────────────

function relativeTime(iso?: string): string {
  if (!iso) return '—'
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60_000)
  if (m < 1)  return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

function duration(start?: string, end?: string): string {
  if (!start || !end) return '—'
  const s = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000)
  if (s < 60) return `${s}s`
  return `${Math.floor(s / 60)}m ${s % 60}s`
}

type HealthLevel = 'healthy' | 'flaky' | 'failing' | 'unknown'

function healthOf(h?: RunbookHealthSummary): HealthLevel {
  if (!h || h.totalJobs === 0) return 'unknown'
  if (h.failRate >= 0.5) return 'failing'
  if (h.failRate >= 0.15 || h.failedJobs > 0) return 'flaky'
  return 'healthy'
}

const HEALTH_COLOR: Record<HealthLevel, string> = {
  healthy: '#10b981', flaky: '#f59e0b', failing: '#ef4444', unknown: '#94a3b8',
}

function HealthIcon({ level }: { level: HealthLevel }) {
  const sx = { fontSize: 15, color: HEALTH_COLOR[level] }
  if (level === 'healthy') return <CheckCircleOutlineIcon sx={sx} />
  if (level === 'flaky')   return <WarningAmberIcon sx={sx} />
  if (level === 'failing') return <ErrorOutlineIcon sx={sx} />
  return <Box sx={{ width: 15, height: 15, borderRadius: '50%', bgcolor: '#e2e8f0', flexShrink: 0 }} />
}

function jobStatusColor(s: string): string {
  if (s === 'Completed') return '#10b981'
  if (s === 'Failed')    return '#ef4444'
  if (s === 'Suspended') return '#f59e0b'
  if (s === 'Running')   return '#3b82f6'
  return '#94a3b8'
}

// ── Job history panel ─────────────────────────────────────────────────────────

function JobHistory({ health }: { health?: RunbookHealthSummary }) {
  if (!health || health.recentJobs.length === 0) {
    return (
      <Box sx={{ px: 3, py: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
          No job history available from Azure Automation.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
      <Box sx={{ px: 3, pt: 2, pb: 2 }}>
        <Typography variant="caption" sx={{
          fontSize: 11, fontWeight: 600, color: '#475569',
          textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 1,
        }}>
          Recent jobs ({health.recentJobs.length})
        </Typography>
        <Stack gap={0}>
          {health.recentJobs.map((job: RunbookJobEntry, i: number) => {
            const color = jobStatusColor(job.status)
            return (
              <Box key={job.jobId || i} sx={{ borderBottom: '1px solid #e2e8f0', '&:last-child': { borderBottom: 'none' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.75 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                  <Chip label={job.status} size="small" sx={{
                    fontSize: 10, height: 20, minWidth: 80, flexShrink: 0,
                    bgcolor: `${color}22`, color, fontWeight: 600, border: 'none',
                  }} />
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1, fontSize: 11 }}>
                    {job.startTime ? new Date(job.startTime).toLocaleString() : '—'}
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, fontSize: 11 }}>
                    {duration(job.startTime, job.endTime)}
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, fontSize: 11, minWidth: 64, textAlign: 'right' }}>
                    {relativeTime(job.endTime ?? job.startTime)}
                  </Typography>
                  {job.portalUrl
                    ? <a href={job.portalUrl} target="_blank" rel="noreferrer"
                        style={{ display: 'flex', color: '#94a3b8', flexShrink: 0 }}>
                        <OpenInNewIcon sx={{ fontSize: 13 }} />
                      </a>
                    : <Box sx={{ width: 13, flexShrink: 0 }} />
                  }
                </Box>
                {job.errorMessage && (
                  <Box sx={{
                    ml: 4, mb: 0.75,
                    bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 1,
                    px: 1.5, py: 1,
                    fontFamily: 'monospace', fontSize: 11, color: '#b91c1c',
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 100, overflow: 'auto',
                  }}>
                    {job.errorMessage}
                  </Box>
                )}
              </Box>
            )
          })}
        </Stack>
      </Box>
    </Box>
  )
}

// ── Runbook row ───────────────────────────────────────────────────────────────

interface RunbookRowProps {
  name: string
  description: string
  canTrigger: boolean
  health?: RunbookHealthSummary
  isSelected: boolean
  onSelect: () => void
  onTrigger: () => void
  isPending: boolean
}

function RunbookRow({ name, description, canTrigger, health, isSelected, onSelect, onTrigger, isPending }: RunbookRowProps) {
  const level = healthOf(health)

  return (
    <>
      <Box
        onClick={onSelect}
        sx={{
          display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 1.5,
          cursor: 'pointer', borderBottom: '1px solid #e2e8f0',
          bgcolor: isSelected ? '#eff6ff' : 'white',
          '&:hover': { bgcolor: isSelected ? '#eff6ff' : '#f8fafc' },
          transition: 'background-color 0.1s',
        }}
      >
        <HealthIcon level={level} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} fontFamily="monospace" sx={{ fontSize: 13 }}>
            {name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
            {description}
          </Typography>
        </Box>

        {health && health.totalJobs > 0 ? (
          <Stack direction="row" gap={2} sx={{ flexShrink: 0 }} alignItems="center">
            <Tooltip title="Fail rate">
              <Typography variant="caption" sx={{ fontSize: 11, color: HEALTH_COLOR[level], fontWeight: 600, minWidth: 32 }}>
                {Math.round(health.failRate * 100)}%
              </Typography>
            </Tooltip>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, minWidth: 60 }}>
              {health.totalJobs} jobs
            </Typography>
            {health.failedJobs > 0 && (
              <Typography variant="caption" sx={{ fontSize: 11, color: '#ef4444', minWidth: 52 }}>
                {health.failedJobs} failed
              </Typography>
            )}
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11, minWidth: 80, textAlign: 'right' }}>
              Last {relativeTime(health.lastJobTime)}
            </Typography>
          </Stack>
        ) : (
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11, flexShrink: 0, minWidth: 160 }}>
            No Azure Automation data
          </Typography>
        )}

        <Tooltip title={canTrigger ? '' : 'No webhook URL configured'}>
          <Box component="span">
            <Button
              size="small" variant="contained" disableElevation
              startIcon={isPending ? <CircularProgress size={11} color="inherit" /> : <PlayArrowIcon />}
              disabled={!canTrigger || isPending}
              onClick={(e: { stopPropagation: () => void }) => { e.stopPropagation(); onTrigger() }}
              sx={{ fontSize: 11, py: 0.5, minWidth: 90, flexShrink: 0 }}
            >
              {isPending ? 'Triggering…' : 'Trigger'}
            </Button>
          </Box>
        </Tooltip>

        <Box sx={{ color: '#94a3b8', flexShrink: 0, fontSize: 16, lineHeight: 1, userSelect: 'none' }}>
          {isSelected ? '▲' : '▼'}
        </Box>
      </Box>

      <Collapse in={isSelected}>
        <JobHistory health={health} />
      </Collapse>
    </>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

interface RowDef { name: string; description: string; canTrigger: boolean }

export default function RunbooksPage() {
  const [selected, setSelected] = useState<string | null>(null)
  const [triggering, setTriggering] = useState<string | null>(null)
  const qc = useQueryClient()

  const { data: runbooksData } = useQuery({
    queryKey: ['runbooks'],
    queryFn: getRunbooks,
  })

  const { data: healthData, isLoading: healthLoading } = useQuery({
    queryKey: ['runbook-health'],
    queryFn: getRunbookHealth,
    refetchInterval: 5 * 60_000,
    staleTime: 2 * 60_000,
  })

  const trigger = useMutation({
    mutationFn: (name: string) => triggerRunbook(name),
    onMutate:  (name: string) => setTriggering(name),
    onSettled: ()             => setTriggering(null),
    onSuccess: ()             => qc.invalidateQueries({ queryKey: ['runbook-health'] }),
  })

  const runbooks: RunbookInfo[] = runbooksData?.data ?? []
  const healthList: RunbookHealthSummary[] = healthData?.data?.runbooks ?? []
  const healthMap: Record<string, RunbookHealthSummary> = Object.fromEntries(
    healthList.map((h: RunbookHealthSummary) => [h.name.toLowerCase(), h])
  )

  const extraNames: string[] = healthList
    .map((h: RunbookHealthSummary) => h.name)
    .filter((n: string) => !runbooks.some((r: RunbookInfo) => r.name.toLowerCase() === n.toLowerCase()))

  const allRows: RowDef[] = [
    ...runbooks.map((r: RunbookInfo) => ({ name: r.name, description: r.description, canTrigger: true })),
    ...extraNames.map((n: string) => ({ name: n, description: 'Not registered for triggering', canTrigger: false })),
  ]

  const healthLevels = allRows.map((r: RowDef) => healthOf(healthMap[r.name.toLowerCase()]))
  const counts = {
    failing: healthLevels.filter((h: HealthLevel) => h === 'failing').length,
    flaky:   healthLevels.filter((h: HealthLevel) => h === 'flaky').length,
    healthy: healthLevels.filter((h: HealthLevel) => h === 'healthy').length,
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5" fontWeight={600}>Runbooks</Typography>
        {!healthLoading && allRows.length > 0 && (
          <Stack direction="row" gap={1} alignItems="center">
            {counts.failing > 0 && <Chip size="small" label={`${counts.failing} failing`}  sx={{ bgcolor: '#fef2f2', color: '#ef4444', fontWeight: 600 }} />}
            {counts.flaky   > 0 && <Chip size="small" label={`${counts.flaky} flaky`}     sx={{ bgcolor: '#fffbeb', color: '#d97706', fontWeight: 600 }} />}
            {counts.healthy > 0 && <Chip size="small" label={`${counts.healthy} healthy`} sx={{ bgcolor: '#f0fdf4', color: '#10b981', fontWeight: 600 }} />}
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>last 200 Automation jobs</Typography>
          </Stack>
        )}
      </Stack>

      {trigger.isError && (
        <Alert severity="error" onClose={() => trigger.reset()}>
          {(trigger.error as Error)?.message}
        </Alert>
      )}
      {trigger.isSuccess && (
        <Alert severity="success" onClose={() => trigger.reset()}>
          Runbook queued successfully.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        {healthLoading && allRows.length === 0 ? (
          <Box sx={{ p: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Loading runbook health…</Typography>
          </Box>
        ) : allRows.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">No runbooks configured.</Typography>
          </Box>
        ) : (
          allRows.map((row: RowDef) => (
            <RunbookRow
              key={row.name}
              name={row.name}
              description={row.description}
              canTrigger={row.canTrigger}
              health={healthMap[row.name.toLowerCase()]}
              isSelected={selected === row.name}
              onSelect={() => setSelected((s: string | null) => s === row.name ? null : row.name)}
              onTrigger={() => trigger.mutate(row.name)}
              isPending={triggering === row.name}
            />
          ))
        )}
      </Paper>
    </Box>
  )
}
